import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { linkifyText } from '../lib/utils';

const MAX_SUGGESTIONS = 6;
const MAX_QUERY_LENGTH = 40;
const MENU_SPACE = 280;
const WORD_CHAR = /[\p{L}\p{N}_]/u;

export function findMentionQuery(text, caret) {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1 || (at > 0 && WORD_CHAR.test(before[at - 1]))) return null;
  const query = before.slice(at + 1);
  if (query.length > MAX_QUERY_LENGTH || /^\s|[\n@]/.test(query)) return null;
  return { start: at, query };
}

export function getMentionSuggestions(candidates, query) {
  if (!candidates) return [];
  const q = query.toLowerCase();
  const residents = (candidates.residents || [])
    .filter(r => r.name && r.name.toLowerCase().includes(q))
    .map(r => ({ key: `user:${r._id}`, label: r.name, detail: r.unit != null ? `Unit ${r.unit}` : null }));
  const units = (candidates.units || [])
    .filter(n => String(n).startsWith(q))
    .map(n => ({ key: `unit:${n}`, label: String(n), detail: 'Everyone in this unit' }));
  return [...residents, ...units].slice(0, MAX_SUGGESTIONS);
}

// Plain-text post/comment content with its saved "@Label" mentions highlighted.
export function MentionText({ text, mentions }) {
  const labels = [...new Set((mentions || []).map(m => m.label))].sort((a, b) => b.length - a.length);
  if (!text || labels.length === 0) return linkifyText(text);

  const lower = text.toLowerCase();
  const parts = [];
  let last = 0;
  for (let i = lower.indexOf('@'); i !== -1; i = lower.indexOf('@', i + 1)) {
    if (i < last || (i > 0 && WORD_CHAR.test(text[i - 1]))) continue;
    const label = labels.find(l => lower.startsWith(l.toLowerCase(), i + 1) && !WORD_CHAR.test(text.charAt(i + 1 + l.length)));
    if (!label) continue;
    if (i > last) parts.push(<React.Fragment key={`t${last}`}>{linkifyText(text.slice(last, i))}</React.Fragment>);
    const end = i + 1 + label.length;
    parts.push(<span key={`m${i}`} className="font-medium text-primary">{text.slice(i, end)}</span>);
    last = end;
  }
  if (last < text.length) parts.push(<React.Fragment key={`t${last}`}>{linkifyText(text.slice(last))}</React.Fragment>);
  return parts;
}

// A textarea that suggests "@resident" and "@unit" mentions from `candidates`
// (only the people who can see the post) as the user types.
const MentionTextarea = forwardRef(function MentionTextarea({ value, onValueChange, candidates, maxLength, onKeyDown, ...props }, ref) {
  const [mention, setMention] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const innerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const suggestions = mention ? getMentionSuggestions(candidates, mention.query) : [];
  const menuOpen = suggestions.length > 0;

  // Fixed to the viewport so cards with overflow-hidden can't clip the menu;
  // it opens upward when there isn't room below the textarea.
  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    const place = () => {
      const el = innerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom >= MENU_SPACE || rect.top < MENU_SPACE;
      setMenuStyle(below
        ? { position: 'fixed', left: rect.left, width: rect.width, top: rect.bottom + 4 }
        : { position: 'fixed', left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 4 });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [menuOpen, value]);

  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const updateMention = (el) => {
    const next = findMentionQuery(el.value, el.selectionStart);
    setMention(prev => (prev?.start === next?.start && prev?.query === next?.query ? prev : next));
    if (next?.query !== mention?.query) setActiveIndex(0);
  };

  const selectSuggestion = (suggestion) => {
    const el = innerRef.current;
    const caret = el ? el.selectionStart : value.length;
    const inserted = `@${suggestion.label} `;
    let next = value.slice(0, mention.start) + inserted + value.slice(caret);
    if (maxLength) next = next.slice(0, maxLength);
    const position = Math.min(mention.start + inserted.length, next.length);
    setMention(null);
    onValueChange(next, el);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(position, position);
    });
  };

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex(i => (i + step + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectSuggestion(suggestions[Math.min(activeIndex, suggestions.length - 1)]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div className="relative">
      <textarea
        {...props}
        ref={setRefs}
        value={value}
        onChange={(e) => {
          onValueChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value, e.target);
          updateMention(e.target);
        }}
        onSelect={(e) => updateMention(e.target)}
        onKeyDown={handleKeyDown}
        onBlur={() => setMention(null)}
        aria-autocomplete="list"
      />
      {menuOpen && (
        <div style={menuStyle || undefined} className="z-50 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          <p className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border">Mention someone who can see this post</p>
          <ul role="listbox" aria-label="Mention suggestions">
            {suggestions.map((s, i) => (
              <li
                key={s.key}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer ${i === activeIndex ? 'bg-muted' : ''}`}
              >
                <span className="font-medium text-foreground truncate">@{s.label}</span>
                {s.detail && <span className="text-xs text-muted-foreground shrink-0">{s.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});

export default MentionTextarea;
